"use client";

import { cn } from "@restomat/shared-utils/utils";
import { Button } from "@restomat/components/client/ui";
import { getPrettyPrice } from "@restomat/shared-utils/utils";
import { useTranslations } from "next-intl";
import { YEARLY_MONTHS_FREE } from "./pricing-data";

/**
 * Summary of the chosen tier. A license carries exactly one tier since #609, so this is a single
 * price rather than a running total of stacked packages.
 */
export default function PriceBreakdown({
  selectedTier,
  billingCycle = "monthly",
  className = "",
}: {
  selectedTier?: {
    id: string;
    title: string;
    price: { monthly: number; yearly: number };
    aiCredits: number;
  };
  billingCycle?: "monthly" | "yearly";
  className?: string;
}) {
  const t = useTranslations("landing.pricing");
  const tCta = useTranslations("landing.cta");

  const monthlyEquivalent = selectedTier
    ? selectedTier.price[billingCycle] / (billingCycle === "yearly" ? 12 : 1)
    : 0;

  const handleTryFree = () => {
    window.location.href = "/auth/login?screen_hint=signup";
  };

  return (
    <div
      data-testid="pricing-breakdown"
      className={cn(
        "flex flex-col items-stretch text-center md:text-right gap-4",
        className,
      )}
    >
      <div className={"text-lg font-black"}>{t("total")}</div>

      {!selectedTier ? (
        <p className="text-xs text-muted-foreground italic">
          {t("selectPackages")}
        </p>
      ) : (
        <>
          <table className={"text-xs"}>
            <tbody>
              <tr>
                <td>{selectedTier.title}</td>
                <td className={"pl-4"}>
                  {getPrettyPrice(Math.round(monthlyEquivalent))} {t("currency")}
                </td>
              </tr>
              <tr>
                <td>{t("aiCreditsLabel")}</td>
                <td className={"pl-4"}>
                  {t("aiCreditsPerMonth", { credits: selectedTier.aiCredits })}
                </td>
              </tr>
            </tbody>
          </table>

          <div
            className={"font-headings text-h2-xl leading-28 3xl:leading-36 mt-4"}
          >
            {getPrettyPrice(Math.round(monthlyEquivalent))} {t("currency")}{" "}
            <span className={"text-lg font-medium"}>{t("perMonth")}</span>
          </div>

          {billingCycle === "yearly" && monthlyEquivalent > 0 && (
            <div className={"font-bold"}>
              {t("yearlyMonthsFree", { months: YEARLY_MONTHS_FREE })}
            </div>
          )}
        </>
      )}

      {/* We are not VAT payers — the listed price is what the customer pays (#609 §7.4). */}
      <p className="text-xs text-muted-foreground">{t("vatNote")}</p>

      <Button className={"w-full"} variant={"secondary"} onClick={handleTryFree}>
        {tCta("startFree")}
      </Button>
    </div>
  );
}
